/**
 * 글로벌몰 주문 출력물(거래명세서·주문내역서) 라벨 SSoT.
 *
 * ★문서 언어 = "주문이 발생한 몰" 기준(뷰어=관리자 UI 언어 아님). 일본몰 주문서=일본어,
 * 영문몰=영어, 중문몰=중국어, 한국몰=한국어. (00-METHODOLOGY §2-1)
 *
 * 기존 BO 출력물은 `useTranslation('orders')` 로 라벨을 관리자 UI 언어로 그렸다(잘못된 축).
 * 또한 admin `orders` 네임스페이스의 `print.*` 블록은 ko-KR 에만 있고 en/ja/zh 는 부재라
 * 비-한국 UI 에서는 raw key 가 노출됐다. 출력물 라벨을 **주문 locale 기준**으로 정확히 그리려면
 * admin UI i18n 과 분리된, locale 별 자급 라벨 사전이 필요하다 → 이 파일.
 *
 * ★한국몰(ko-KR) 라벨은 기존 public/locales/ko-KR/orders.json 의 `orders.print.*` 와
 * payment-method enumLabels(PAYMENT_METHOD_LABELS) 를 **그대로** 옮겨 byte-identical 회귀 0.
 * SF features/order-documents 의 라벨 카피와 동일 의미(라벨 SSoT 정합).
 *
 * design-i18n 산출물이 도착하면 이 사전을 교체/보강한다(구조 동일, 카피만).
 */

import type { Money } from '@/lib/api-admin/types';

/** be-order print DTO 의 localeCode(order_mall → BCP-47). 4 글로벌몰. */
export type DocumentLocaleCode = 'ko-KR' | 'ja-JP' | 'en-US' | 'zh-CN';

export const DOCUMENT_LOCALES: readonly DocumentLocaleCode[] = [
  'ko-KR',
  'ja-JP',
  'en-US',
  'zh-CN',
] as const;

const DEFAULT_LOCALE: DocumentLocaleCode = 'ko-KR';

/** 통화 → 몰 locale 폴백(BE localeCode 미공급 시 결제통화로 best-effort 도출). */
const CURRENCY_TO_LOCALE: Readonly<Record<string, DocumentLocaleCode>> = {
  KRW: 'ko-KR',
  JPY: 'ja-JP',
  USD: 'en-US',
  CNY: 'zh-CN',
};

/**
 * 임의 문자열(BE localeCode/언어 코드/통화)을 지원 locale 로 정규화한다.
 * 우선순위: 정확한 BCP-47 → 언어 prefix(ko/ja/en/zh) → 통화 폴백 → ko-KR.
 * raw 값을 화면에 노출하지 않고 항상 지원 locale 로 떨어뜨린다(회귀 가드).
 */
export const normalizeDocumentLocale = (
  value: string | null | undefined,
): DocumentLocaleCode => {
  if (!value) return DEFAULT_LOCALE;
  const exact = DOCUMENT_LOCALES.find((l) => l.toLowerCase() === value.toLowerCase());
  if (exact) return exact;
  const lang = value.toLowerCase().split(/[-_]/)[0];
  switch (lang) {
    case 'ko':
      return 'ko-KR';
    case 'ja':
      return 'ja-JP';
    case 'en':
      return 'en-US';
    case 'zh':
      return 'zh-CN';
    default:
      return DEFAULT_LOCALE;
  }
};

/** 결제통화 코드로 몰 locale 를 추정(localeCode 미공급 폴백용). */
export const localeFromCurrency = (
  currencyCode: string | null | undefined,
): DocumentLocaleCode | null =>
  currencyCode ? (CURRENCY_TO_LOCALE[currencyCode.toUpperCase()] ?? null) : null;

/** 출력물 라벨 묶음 — 4 locale 전부 동일 키 셋(타입으로 누락 방지). */
export interface PrintLabelSet {
  printButton: string;
  close: string;
  loading: string;
  error: string;
  shopName: string;
  kind: {
    'transaction-statement': string;
    'order-statement': string;
  };
  field: {
    orderNo: string;
    issuedAt: string;
    ordererName: string;
    ordererMobile: string;
    recipient: string;
    recipientPhone: string;
    recipientEnglishName: string;
    recipientMobile: string;
    remarks: string;
    shippingAddress: string;
    country: string;
  };
  col: {
    image: string;
    code: string;
    productName: string;
    vendor: string;
    option: string;
    qty: string;
    unitPrice: string;
    lineAmount: string;
    /** QA-517 해외몰 주문 — 원화 환산 금액 칸. */
    lineAmountKrw: string;
  };
  summary: {
    subtotal: string;
    shippingFee: string;
    discount: string;
    total: string;
  };
  payment: {
    method: string;
    paidAt: string;
    mileageUsed: string;
    depositUsed: string;
    payable: string;
    /** QA-517 해외몰 주문 — 결제 요약의 원화 칸 제목. */
    krw: string;
  };
  transactionNote: string;
  /** [233] 주문서 추가정보(통관 등) 섹션 제목 + 마스킹 배지(주문 locale 기준). */
  orderFormFieldValues: {
    title: string;
    maskedBadge: string;
  };
  /** 결제수단 enum → 라벨(주문 locale 기준). admin-only enumLabels 대체. */
  paymentMethod: Readonly<Record<string, string>>;
}

const KO: PrintLabelSet = {
  printButton: '인쇄',
  close: '닫기',
  loading: '출력물을 불러오는 중...',
  error: '출력물을 불러오지 못했습니다.',
  shopName: '에버라인',
  kind: {
    'transaction-statement': '거래명세서',
    'order-statement': '주문내역서',
  },
  field: {
    orderNo: '주문번호',
    issuedAt: '발행일시',
    ordererName: '주문자',
    ordererMobile: '연락처',
    recipient: '수령인',
    recipientPhone: '수령인 연락처',
    recipientEnglishName: '수령인(영문)',
    recipientMobile: '수령인 휴대폰',
    remarks: '비고',
    shippingAddress: '배송지',
    country: '국가',
  },
  col: {
    image: '이미지',
    code: '상품코드',
    productName: '상품명',
    vendor: '공급사',
    option: '옵션',
    qty: '수량',
    unitPrice: '판매가',
    lineAmount: '금액',
    lineAmountKrw: '원화 금액',
  },
  summary: {
    subtotal: '상품 합계',
    shippingFee: '배송비',
    discount: '할인',
    total: '합계',
  },
  payment: {
    method: '결제수단',
    paidAt: '결제일시',
    mileageUsed: '마일리지 사용',
    depositUsed: '예치금 사용',
    payable: '결제금액',
    krw: '원화(KRW)',
  },
  transactionNote:
    '합계 금액은 배송비와 할인만 반영되며, 마일리지·예치금은 포함되지 않습니다.',
  orderFormFieldValues: {
    title: '주문서 추가정보',
    maskedBadge: '마스킹',
  },
  paymentMethod: {
    CARD: '신용/체크카드',
    VIRTUAL_ACCOUNT: '가상계좌',
    KAKAOPAY: '카카오페이',
    TOSSPAY: '토스페이',
    BANK_TRANSFER: '무통장입금',
    FOREIGN_EASY_PAY: '해외 간편결제',
    PAYPAL: 'PayPal',
    PAYPAY: 'PayPay',
    ALIPAY: 'Alipay',
  },
};

const JA: PrintLabelSet = {
  printButton: '印刷',
  close: '閉じる',
  loading: '帳票を読み込み中...',
  error: '帳票を読み込めませんでした。',
  shopName: 'エバーライン',
  kind: {
    'transaction-statement': '取引明細書',
    'order-statement': 'ご注文明細書',
  },
  field: {
    orderNo: 'ご注文番号',
    issuedAt: '発行日時',
    ordererName: 'ご注文者',
    ordererMobile: '電話番号',
    recipient: 'お届け先',
    recipientPhone: '電話番号',
    recipientEnglishName: 'お届け先(英字)',
    recipientMobile: '携帯電話',
    remarks: '配送メモ',
    shippingAddress: 'ご住所',
    country: '国',
  },
  col: {
    image: '画像',
    code: '商品コード',
    productName: '商品',
    vendor: '供給会社',
    option: 'オプション',
    qty: '数量',
    unitPrice: '単価',
    lineAmount: '金額',
    lineAmountKrw: 'ウォン換算金額',
  },
  summary: {
    subtotal: '商品小計',
    shippingFee: '送料',
    discount: '割引額',
    total: '合計金額',
  },
  payment: {
    method: 'お支払い方法',
    paidAt: 'お支払い日時',
    mileageUsed: 'マイレージ利用',
    depositUsed: '預り金利用',
    payable: 'お支払い金額',
    krw: 'ウォン(KRW)',
  },
  transactionNote: '本明細書は取引内容のご確認用です。',
  orderFormFieldValues: {
    title: 'ご注文時の追加情報',
    maskedBadge: 'マスキング',
  },
  paymentMethod: {
    CARD: 'クレジット/デビットカード',
    VIRTUAL_ACCOUNT: 'バーチャル口座',
    KAKAOPAY: 'カカオペイ',
    TOSSPAY: 'トスペイ',
    BANK_TRANSFER: '銀行振込',
    FOREIGN_EASY_PAY: '海外かんたん決済',
    PAYPAL: 'PayPal',
    PAYPAY: 'PayPay',
    ALIPAY: 'Alipay',
  },
};

const EN: PrintLabelSet = {
  printButton: 'Print',
  close: 'Close',
  loading: 'Loading document...',
  error: 'Failed to load the document.',
  shopName: 'EVERLINE',
  kind: {
    'transaction-statement': 'Statement of Transaction',
    'order-statement': 'Order Statement',
  },
  field: {
    orderNo: 'Order No.',
    issuedAt: 'Issued',
    ordererName: 'Orderer',
    ordererMobile: 'Phone',
    recipient: 'Recipient',
    recipientPhone: 'Phone',
    recipientEnglishName: 'Recipient (English)',
    recipientMobile: 'Mobile',
    remarks: 'Delivery Note',
    shippingAddress: 'Address',
    country: 'Country',
  },
  col: {
    image: 'Image',
    code: 'Product Code',
    productName: 'Product',
    vendor: 'Supplier',
    option: 'Option',
    qty: 'Qty',
    unitPrice: 'Unit Price',
    lineAmount: 'Amount',
    lineAmountKrw: 'Amount (KRW)',
  },
  summary: {
    subtotal: 'Subtotal',
    shippingFee: 'Shipping Fee',
    discount: 'Discount',
    total: 'Total',
  },
  payment: {
    method: 'Payment Method',
    paidAt: 'Paid At',
    mileageUsed: 'Mileage Used',
    depositUsed: 'Deposit Used',
    payable: 'Amount Paid',
    krw: 'KRW',
  },
  transactionNote: 'This statement is for confirming transaction details.',
  orderFormFieldValues: {
    title: 'Additional Order Information',
    maskedBadge: 'Masked',
  },
  paymentMethod: {
    CARD: 'Credit/Debit Card',
    VIRTUAL_ACCOUNT: 'Virtual Account',
    KAKAOPAY: 'KakaoPay',
    TOSSPAY: 'TossPay',
    BANK_TRANSFER: 'Bank Transfer',
    FOREIGN_EASY_PAY: 'International Easy Pay',
    PAYPAL: 'PayPal',
    PAYPAY: 'PayPay',
    ALIPAY: 'Alipay',
  },
};

const ZH: PrintLabelSet = {
  printButton: '打印',
  close: '关闭',
  loading: '正在加载单据...',
  error: '无法加载单据。',
  shopName: 'EVERLINE',
  kind: {
    'transaction-statement': '交易明细单',
    'order-statement': '订单明细单',
  },
  field: {
    orderNo: '订单编号',
    issuedAt: '开具时间',
    ordererName: '订购人',
    ordererMobile: '联系电话',
    recipient: '收货人',
    recipientPhone: '联系电话',
    recipientEnglishName: '收货人(英文)',
    recipientMobile: '手机号码',
    remarks: '配送备注',
    shippingAddress: '地址',
    country: '国家',
  },
  col: {
    image: '图片',
    code: '商品编码',
    productName: '商品',
    vendor: '供应商',
    option: '选项',
    qty: '数量',
    unitPrice: '单价',
    lineAmount: '金额',
    lineAmountKrw: '韩元金额',
  },
  summary: {
    subtotal: '商品小计',
    shippingFee: '运费',
    discount: '折扣金额',
    total: '合计金额',
  },
  payment: {
    method: '支付方式',
    paidAt: '支付时间',
    mileageUsed: '使用积分',
    depositUsed: '使用储值金',
    payable: '支付金额',
    krw: '韩元(KRW)',
  },
  transactionNote: '本明细单仅供确认交易内容之用。',
  orderFormFieldValues: {
    title: '订单附加信息',
    maskedBadge: '掩码',
  },
  paymentMethod: {
    CARD: '信用卡/借记卡',
    VIRTUAL_ACCOUNT: '虚拟账户',
    KAKAOPAY: 'KakaoPay',
    TOSSPAY: 'TossPay',
    BANK_TRANSFER: '银行转账',
    FOREIGN_EASY_PAY: '海外便捷支付',
    PAYPAL: 'PayPal',
    PAYPAY: 'PayPay',
    ALIPAY: 'Alipay',
  },
};

const LABELS: Readonly<Record<DocumentLocaleCode, PrintLabelSet>> = {
  'ko-KR': KO,
  'ja-JP': JA,
  'en-US': EN,
  'zh-CN': ZH,
};

/** 주문 locale 의 라벨 묶음을 반환한다(미지원 locale 은 ko-KR 폴백). */
export const printLabelsFor = (locale: DocumentLocaleCode): PrintLabelSet =>
  LABELS[locale] ?? KO;

/** 결제수단 enum → 주문 locale 라벨. 미지원/null 은 '-'(raw key 비노출). */
export const paymentMethodLabel = (
  locale: DocumentLocaleCode,
  method: string | null | undefined,
): string => {
  if (!method) return '-';
  return printLabelsFor(locale).paymentMethod[method] ?? method;
};

/**
 * 결제통화·주문 locale 기준 금액 포맷 — locale-aware Intl currency 단일 경로(Lead 결정 2).
 *
 * locale 이 통화기호·천단위 구분자·소수자리를 동시에 결정(KRW/JPY=0, USD/CNY=2 통화 기본 자릿수에 위임).
 * 예: ko-KR/KRW → ₩21,500 · ja-JP/JPY → ￥3,500 · en-US/USD → $24.50 · zh-CN/CNY → ¥188.00.
 * ★한국몰도 종전 `21,500 KRW` → `₩21,500` 으로 통화기호 표기(의도된 개선, 결제통화 단일 표기 정합).
 * SF features/order-documents/lib/format 과 동일 규칙. Intl 실패 시 천단위 + 통화코드 폴백.
 */
export const formatMoney = (
  money: Money | null | undefined,
  locale: DocumentLocaleCode,
): string => {
  if (!money) return '-';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: money.currencyCode,
    }).format(money.amount);
  } catch {
    return `${money.amount.toLocaleString(locale)} ${money.currencyCode}`;
  }
};
